import { useEffect } from "react";
import { api } from "../lib/api";
import { apiErrorCode } from "../lib/apiError";
// #2094 — `saved_at` comes back as a NAIVE datetime (the backend types it as a
// bare `dt.datetime`, UTC in practice, and serialises it with no offset). A
// bare `new Date()` would read that as LOCAL time and skew the AUTOSAVED label
// by the archer's whole UTC offset. Same fix #2031 applied to `completed_at`.
import { parseUTC } from "../lib/dates";

// Build the per-arrow payload sent to the API (confirm + autosave share it).
// `method` is derived PER ARROW from position presence, not from the active
// tab (#1837): a mixed end (tab switched mid-end) would otherwise stamp
// keypad arrows `method="drop"`, breaking the backend contract that only
// drop-placed arrows carry a position / are spotted by the multi-spot rule.
// The active tab is therefore NOT an input here — the `mode` parameter every
// call site used to pass had been unread since #1837 and is gone (#2177).
export function arrowPayload(current) {
  return current.arrows.map((s, i) => {
    const p = current.positions[i];
    const placed = p?.x != null && p?.y != null;
    return {
      score: String(s),
      x: placed ? p.x : null,
      y: placed ? p.y : null,
      method: placed ? "drop" : "tap",
    };
  });
}

// Re-save the in-progress end at least this often, as a safety net behind
// the per-arrow autosave.
const SAFETY_SAVE_MS = 25000;

// Baseline debounce for the per-arrow autosave on the happy path.
const AUTOSAVE_BASE_MS = 600;
// Cap for the exponential backoff applied after consecutive autosave failures.
const AUTOSAVE_BACKOFF_CAP_MS = 5000;

/**
 * #1210 — exponential backoff for the per-arrow autosave.
 *
 * On the happy path (failCount 0) the autosave fires at the baseline debounce.
 * Each consecutive failure backs the next attempt off: 1s → 2s → 4s, capped at
 * 5s. A successful save resets failCount to 0, returning to the baseline. This
 * stops a retry-storm feedback loop under DB write contention: the busier the
 * backend, the *less* often each client retries, not more.
 */
export function autosaveDelayMs(failCount) {
  if (!failCount || failCount < 1) return AUTOSAVE_BASE_MS;
  const backoff = 1000 * 2 ** (failCount - 1); // 1s, 2s, 4s, 8s, …
  return Math.min(backoff, AUTOSAVE_BACKOFF_CAP_MS);
}

/**
 * #1210 — content dedup key for the autosave payload.
 *
 * A stable serialization of the per-arrow payload so an autosave can be skipped
 * entirely when nothing has changed since the last successful save. Includes
 * ends_done so an end-boundary advance is never treated as "unchanged".
 */
export function autosaveContentKey(payload, endsDone) {
  return JSON.stringify({ e: endsDone, a: payload });
}

/**
 * useAutosave — persists the in-progress end so a refresh loses no data.
 *
 * Mechanical extraction of the two near-duplicate autosave effects from
 * Score.jsx: a per-arrow debounced save and a periodic safety-timer save.
 * Both share the autosave control refs and the dedup/backoff logic. Behavior is
 * preserved verbatim — the concurrency guards (paused / confirmInFlight /
 * localChange / failCount / content dedup) are load-bearing and unchanged.
 *
 * #2061 — the control refs now arrive as ONE session-owned `refs` bundle from
 * useScoringSession rather than seven caller-owned refs, because the rest of the
 * session mutates them too (round-load reset, confirmEnd, score entry, live bale
 * sync). The hook reads them; it does not own them.
 */
export function useAutosave({
  round,
  // QA 2026-09-18 (sandbox round 492): a completed round — fixed at its last
  // end, open-ended at the OPEN_ENDED_MAX_ENDS cap or finished — has no
  // in-progress end to persist, and the server 409s `round_already_complete`
  // on every draft POST. Both effects below are skipped outright while true,
  // so a fresh load of `/score?round=<done>` never fires the empty
  // `{arrows: [], expected_ends_done: N}` save that used to land in the
  // console as a 409. Defaults to false so existing callers are unchanged.
  roundComplete = false,
  current,
  mode,
  editingEnd,
  isViewer,
  refs,
  setAutosaveWarning,
  setLastSaved,
}) {
  const {
    liveRef,
    autosavePausedRef,
    autosaveFailCountRef,
    lastSavedContentRef,
    localChangeRef,
    confirmInFlightRef,
  } = refs;

  // ─── per-arrow autosave (debounced) — a refresh loses no data ───
  // The draft is the in-progress *new* end; while editing an earlier end in
  // place there is no draft to keep, so autosave pauses.
  useEffect(() => {
    if (!round || editingEnd != null) return undefined;
    if (isViewer) return undefined;  // viewers never write
    if (roundComplete) return undefined;  // nothing left to draft; server would 409
    // #1210 — back the debounce off after consecutive failures so a stalled
    // backend isn't hammered at the keystroke rate.
    const delay = autosaveDelayMs(autosaveFailCountRef.current);
    const handle = setTimeout(() => {
      if (autosavePausedRef.current) return;
      if (confirmInFlightRef.current) return;  // #1285 — don't autosave a confirming end
      const payload = arrowPayload(current);
      // #1210 — content dedup: skip the POST entirely when nothing changed
      // since the last successful save.
      const contentKey = autosaveContentKey(payload, round.ends_done);
      if (contentKey === lastSavedContentRef.current) return;
      api
        .saveDraft(round.id, payload, round.ends_done)
        .then((d) => {
          autosavePausedRef.current = false;
          autosaveFailCountRef.current = 0;
          lastSavedContentRef.current = contentKey;
          setAutosaveWarning(false);
          localChangeRef.current = false;
          setLastSaved(parseUTC(d.saved_at));
        })
        .catch((err) => {
          localChangeRef.current = false;
          if (err?.status === 403) return;  // stale save in viewer mode — ignore silently
          if (err?.status === 409 && apiErrorCode(err) === "stale_end") return;
          if (err?.status === 404 || err?.status === 409) {
            autosavePausedRef.current = true;
          }
          autosaveFailCountRef.current += 1;
          if (autosaveFailCountRef.current > 1) {
            setAutosaveWarning(true);
          }
        });
    }, delay);
    return () => clearTimeout(handle);
  }, [round, current, mode, editingEnd, isViewer, roundComplete]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── timer-based safety save — re-persists the end periodically ───
  useEffect(() => {
    if (!round) return undefined;
    if (isViewer) return undefined;  // viewers never write
    if (roundComplete) return undefined;  // nothing left to draft; server would 409
    const id = setInterval(() => {
      if (autosavePausedRef.current) return;
      if (confirmInFlightRef.current) return;  // #1285 — don't autosave a confirming end
      const { round: r, current: c, editingEnd: e } = liveRef.current;
      if (!r || e != null || c.arrows.length === 0) return;
      const payload = arrowPayload(c);
      // #1210 — share the dedup key with the per-arrow autosave so the safety
      // timer doesn't re-POST identical content either.
      const contentKey = autosaveContentKey(payload, r.ends_done);
      if (contentKey === lastSavedContentRef.current) return;
      api
        .saveDraft(r.id, payload, r.ends_done)
        .then((d) => {
          autosavePausedRef.current = false;
          autosaveFailCountRef.current = 0;
          lastSavedContentRef.current = contentKey;
          setAutosaveWarning(false);
          localChangeRef.current = false;
          setLastSaved(parseUTC(d.saved_at));
        })
        .catch((err) => {
          localChangeRef.current = false;
          if (err?.status === 403) return;  // stale save in viewer mode — ignore silently
          if (err?.status === 409 && apiErrorCode(err) === "stale_end") return;
          if (err?.status === 404 || err?.status === 409) {
            autosavePausedRef.current = true;
          }
          autosaveFailCountRef.current += 1;
          if (autosaveFailCountRef.current > 1) {
            setAutosaveWarning(true);
          }
        });
    }, SAFETY_SAVE_MS);
    return () => clearInterval(id);
  }, [round, isViewer, roundComplete]); // eslint-disable-line react-hooks/exhaustive-deps
}
